/**
 * Order Flows Page
 * 
 * Analyzes order processing times between status transitions
 * Shows P90, median, histograms, and decile tables for each step
 */

import { parseISO, format } from 'date-fns'
import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { orderFlowsAPI } from '../services/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { DateRangePicker } from '../components/DateRangePicker'
import ClinicMultiSelect from '../components/ClinicMultiSelect'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line
} from 'recharts'

// Format duration as hours or days
function formatDuration(hours) {
  if (hours === null || hours === undefined || isNaN(hours)) return 'N/A'
  if (hours > 100) {
    const days = hours / 24
    return `${days.toFixed(1)} days`
  }
  return `${hours.toFixed(2)} hrs`
}

// Step colors for charts
const STEP_COLORS = {
  'Time_to_Route': '#06b6d4', // Cyan
  'Time_at_Doctor': '#3b82f6', // Blue
  'Time_at_Pharmacy': '#8b5cf6', // Purple
  'Time_in_Shipping': '#22c55e', // Green
  'Total_Order_Time': '#f59e0b', // Amber
  'Time_to_Cancel': '#ef4444', // Red
}

// Statistics the per-clinic Total Order Time trend can plot
const CLINIC_TREND_STATS = [
  { key: 'p50', label: 'P50 (Median)' },
  { key: 'p75', label: 'P75' },
  { key: 'p90', label: 'P90' },
  { key: 'avg', label: 'Average' },
]

export default function OrderFlowsPage() {
  const { currentClinicId } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Date state
  const [dateRange, setDateRange] = useState(() => {
    const startParam = searchParams.get('startDate')
    const endParam = searchParams.get('endDate')
    if (startParam && endParam) {
      return { from: parseISO(startParam), to: parseISO(endParam) }
    }
    // Default to last 7 days
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 7)
    return { from: start, to: end }
  })
  
  // Comparison state
  const [enableComparison, setEnableComparison] = useState(false)
  const [compDateRange, setCompDateRange] = useState(() => {
    const end = new Date(dateRange.from)
    end.setDate(end.getDate() - 1)
    const start = new Date(end)
    start.setDate(start.getDate() - 7)
    return { from: start, to: end }
  })
  
  // Filter state
  const [filters, setFilters] = useState({ drugs: [], pharmacies: [] })
  const [selectedDrugs, setSelectedDrugs] = useState([])
  const [selectedPharmacies, setSelectedPharmacies] = useState([])
  const [excludeWeekends, setExcludeWeekends] = useState(true)
  
  // Tab state
  const [activeTab, setActiveTab] = useState('analysis') // 'analysis' or 'trends'

  // Data state
  const [stepData, setStepData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expandedSteps, setExpandedSteps] = useState({})
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  // Duration trends state
  const [trendData, setTrendData] = useState(null)
  const [trendLoading, setTrendLoading] = useState(false)
  const [trendPercentile, setTrendPercentile] = useState('p50')
  const [clinicTrendStat, setClinicTrendStat] = useState('p50')

  // Clinic state — core clinics the user can pool. An empty selection means the
  // tab's own clinic (how a non-core clinic like MDExam legacy is still served).
  const [coreClinics, setCoreClinics] = useState([])
  const [selectedClinics, setSelectedClinics] = useState(() =>
    (searchParams.get('clinics') || '').split(',').filter(Boolean)
  )
  const tabIsCore = coreClinics.some(c => c.id === currentClinicId)
  const clinicsParam = selectedClinics.length > 0 ? selectedClinics.join(',') : null
  const noClinicSelected = selectedClinics.length === 0 && (tabIsCore || !currentClinicId)

  useEffect(() => {
    orderFlowsAPI.getClinics()
      .then(response => {
        if (!response.data?.success) return
        const clinics = response.data.data.clinics.map(c => ({ id: c.clinicId, displayName: c.displayName, color: c.color }))
        setCoreClinics(clinics)
        // Default to the tab's clinic when it is core and nothing came from the URL;
        // with no tab clinic at all, default to every core clinic.
        setSelectedClinics(prev => {
          const known = prev.filter(id => clinics.some(c => c.id === id))
          if (known.length > 0) return known
          if (!currentClinicId) return clinics.map(c => c.id)
          return clinics.some(c => c.id === currentClinicId) ? [currentClinicId] : []
        })
      })
      .catch(err => console.error('[ORDER_FLOWS] Error fetching clinics:', err))
  }, [currentClinicId])

  // Fetch filter options
  useEffect(() => {
    async function fetchFilters() {
      try {
        const response = await orderFlowsAPI.getFilters(clinicsParam ? { clinics: clinicsParam } : {})
        if (response.data?.success) {
          setFilters(response.data.data)
        }
      } catch (err) {
        console.error('[ORDER_FLOWS] Error fetching filters:', err)
      }
    }

    if ((currentClinicId || clinicsParam) && !noClinicSelected) {
      fetchFilters()
    }
  }, [currentClinicId, clinicsParam, noClinicSelected])

  // Fetch step analysis data
  const fetchData = useCallback(async () => {
    if ((!currentClinicId && !clinicsParam) || noClinicSelected) {
      setError('Please select at least one clinic')
      setLoading(false)
      return
    }
    
    if (!dateRange.from || !dateRange.to) {
      setError('Please select a date range')
      setLoading(false)
      return
    }
    
    setLoading(true)
    setError(null)
    setStepData(null)
    
    try {
      const params = {
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
      }
      if (clinicsParam) params.clinics = clinicsParam
      
      // Add comparison dates if enabled
      if (enableComparison && compDateRange.from && compDateRange.to) {
        params.compStartDate = format(compDateRange.from, 'yyyy-MM-dd')
        params.compEndDate = format(compDateRange.to, 'yyyy-MM-dd')
      }
      
      // Add filters
      if (selectedDrugs.length > 0) {
        params.drugs = selectedDrugs.join(',')
      }
      if (selectedPharmacies.length > 0) {
        params.pharmacies = selectedPharmacies.join(',')
      }
      if (!excludeWeekends) {
        params.excludeWeekends = 'false'
      }

      console.log('[ORDER_FLOWS] Fetching with params:', params)
      const response = await orderFlowsAPI.getStepAnalysis(params)
      
      if (response.data?.success) {
        setStepData(response.data.data)
        setHasLoadedOnce(true)
      } else {
        setError(response.data?.error || 'Failed to fetch data')
      }
    } catch (err) {
      console.error('[ORDER_FLOWS] Error fetching data:', err)
      const errorMsg = err.code === 'ECONNABORTED' 
        ? 'Request timed out - try a smaller date range or add filters'
        : err.response?.data?.message || err.response?.data?.error?.message || err.response?.data?.error || err.message || 'Failed to fetch order flow data'
      setError(errorMsg)
    } finally {
      setLoading(false)
    }
  }, [currentClinicId, clinicsParam, noClinicSelected, dateRange, enableComparison, compDateRange, selectedDrugs, selectedPharmacies, excludeWeekends])

  // Handle apply filters button click
  const handleApplyFilters = () => {
    // Update URL params
    if (dateRange.from && dateRange.to) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('startDate', format(dateRange.from, 'yyyy-MM-dd'))
      newParams.set('endDate', format(dateRange.to, 'yyyy-MM-dd'))
      if (clinicsParam) newParams.set('clinics', clinicsParam)
      else newParams.delete('clinics')
      setSearchParams(newParams, { replace: true })
    }
    fetchData()
  }

  // No automatic fetch - user must click "Apply Filters" button

  // Fetch duration trends when trends tab is active
  const fetchTrends = useCallback(async () => {
    if ((!currentClinicId && !clinicsParam) || noClinicSelected || !dateRange.from || !dateRange.to) return

    setTrendLoading(true)
    try {
      const params = {
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
      }
      if (clinicsParam) params.clinics = clinicsParam
      if (selectedDrugs.length > 0) params.drugs = selectedDrugs.join(',')
      if (selectedPharmacies.length > 0) params.pharmacies = selectedPharmacies.join(',')
      if (!excludeWeekends) params.excludeWeekends = 'false'

      const response = await orderFlowsAPI.getDurationTrends(params)
      if (response.data?.success) {
        setTrendData(response.data.data)
      }
    } catch (err) {
      console.error('[ORDER_FLOWS] Error fetching duration trends:', err)
    } finally {
      setTrendLoading(false)
    }
  }, [currentClinicId, clinicsParam, noClinicSelected, dateRange, selectedDrugs, selectedPharmacies, excludeWeekends])

  useEffect(() => {
    if (activeTab === 'trends') {
      fetchTrends()
    }
  }, [activeTab, fetchTrends])

  // Toggle step expansion
  const toggleStepExpand = (stepKey) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepKey]: !prev[stepKey]
    }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Order Flow Analysis</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Processing time analysis across order workflow stages
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative group flex items-center gap-2">
            <label htmlFor="exclude-weekends" className="text-sm font-medium text-foreground cursor-pointer select-none">
              Exclude Weekends
            </label>
            <button
              id="exclude-weekends"
              role="switch"
              aria-checked={excludeWeekends}
              onClick={() => setExcludeWeekends(v => !v)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                excludeWeekends ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                  excludeWeekends ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-md bg-popover border border-border shadow-md text-xs text-popover-foreground w-64 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
              Subtracts Fri 8 PM – Mon 7 AM ET dead time from step durations. Orders sitting idle over the weekend won't inflate processing metrics.
              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-border" />
            </div>
          </div>
          <Button onClick={handleApplyFilters} disabled={loading} variant="outline" size="sm">
            {loading ? (
              <>
                <span className="animate-spin mr-2">⟳</span>
                Loading...
              </>
            ) : (
              <>
                <span className="mr-2">↻</span>
                Refresh
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filters Row */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Clinic Checklist */}
            {coreClinics.length > 0 && (
              <ClinicMultiSelect
                clinics={coreClinics}
                selected={selectedClinics}
                onChange={setSelectedClinics}
              />
            )}

            {/* Primary Date Range */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Date Range</label>
              <DateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            </div>
            
            {/* Comparison Toggle */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enable-comparison"
                checked={enableComparison}
                onChange={(e) => setEnableComparison(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="enable-comparison" className="text-sm font-medium text-foreground">
                Compare to period
              </label>
            </div>
            
            {/* Comparison Date Range */}
            {enableComparison && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Comparison Period</label>
                <DateRangePicker
                  dateRange={compDateRange}
                  onDateRangeChange={setCompDateRange}
                />
              </div>
            )}
            
            {/* Drug Filter */}
            <div className="space-y-2 min-w-[200px]">
              <label className="text-sm font-medium text-foreground">Drug Filter</label>
              <Select 
                value={selectedDrugs.length === 0 ? 'all' : selectedDrugs[0]}
                onValueChange={(val) => setSelectedDrugs(val === 'all' ? [] : [val])}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Drugs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Drugs</SelectItem>
                  {filters.drugs?.map(drug => (
                    <SelectItem key={drug} value={drug}>{drug}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Pharmacy Filter */}
            <div className="space-y-2 min-w-[200px]">
              <label className="text-sm font-medium text-foreground">Pharmacy Filter</label>
              <Select 
                value={selectedPharmacies.length === 0 ? 'all' : selectedPharmacies[0]}
                onValueChange={(val) => setSelectedPharmacies(val === 'all' ? [] : [val])}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Pharmacies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Pharmacies</SelectItem>
                  {filters.pharmacies?.map(pharmacy => (
                    <SelectItem key={pharmacy} value={pharmacy}>{pharmacy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Apply Filters Button */}
            <div className="flex items-end">
              <Button 
                onClick={handleApplyFilters} 
                disabled={loading || !dateRange.from || !dateRange.to}
                className="h-10"
              >
                {loading ? (
                  <>
                    <span className="animate-spin mr-2">⟳</span>
                    Loading...
                  </>
                ) : (
                  <>
                    <span className="mr-2">▶</span>
                    Run Analysis
                  </>
                )}
              </Button>
            </div>
          </div>
          {selectedClinics.length === 0 && currentClinicId && !tabIsCore && coreClinics.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              Showing this tab's clinic on its own — it isn't a core clinic, so it can't be pooled. Pick core clinics above to analyze them instead.
            </p>
          )}
          {selectedClinics.length > 1 && (
            <p className="text-xs text-muted-foreground mt-3">
              Stats pool every order across the {selectedClinics.length} selected clinics — a P90 here is the P90 of all their orders, not an average of each clinic's P90.
            </p>
          )}
        </CardContent>
      </Card>

      {[...(stepData?.metadata?.errors || []), ...(trendData?.metadata?.errors || [])]
        .filter((e, i, all) => all.findIndex(x => x.clinicId === e.clinicId) === i)
        .map(e => (
          <p key={e.clinicId} className="text-sm text-destructive">
            <span className="font-medium">{e.clinicId}</span> could not be read and is left out: {e.error}
          </p>
        ))}

      {/* Tab Switcher */}
      <div className="flex gap-2 border-b border-border pb-2">
        <button
          onClick={() => setActiveTab('analysis')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'analysis'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          Step Analysis
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'trends'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          Duration Trends
        </button>
      </div>

      {/* ─── Step Analysis Tab ─── */}
      {activeTab === 'analysis' && (
      <>
      {/* Error Display */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Initial State - No Data Loaded Yet */}
      {!stepData && !hasLoadedOnce && !error && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-foreground mb-2">Ready to Analyze</h3>
              <p className="text-muted-foreground mb-4">
                Select your date range and filters above, then click "Run Analysis" to view order flow timing data.
              </p>
              <Button onClick={handleApplyFilters} disabled={!dateRange.from || !dateRange.to}>
                <span className="mr-2">▶</span>
                Run Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading skeleton when no prior data */}
      {loading && !stepData && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="h-24 bg-muted rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Overview Chart - Stacked Bar showing combined step timings */}
      {stepData && (
        <Card>
          <CardHeader>
            <CardTitle>Order Processing Time by Step</CardTitle>
            <CardDescription>
              Stacked view showing P90, Median, and Average times for each workflow step
            </CardDescription>
          </CardHeader>
          <CardContent>
            <StackedTimingChart stepData={stepData} />
          </CardContent>
        </Card>
      )}

      {/* Per-Clinic Breakdown */}
      {stepData && stepData.metadata?.clinics?.length > 1 && (
        <ClinicBreakdownCard stepData={stepData} enableComparison={enableComparison} />
      )}

      {/* Per-Step Analysis Sections */}
      {stepData && Object.entries(stepData.steps).map(([stepKey, data]) => (
        <StepAnalysisCard
          key={stepKey}
          stepKey={stepKey}
          data={data}
          enableComparison={enableComparison}
          expanded={expandedSteps[stepKey] || false}
          onToggle={() => toggleStepExpand(stepKey)}
          dateRange={dateRange}
          compDateRange={compDateRange}
        />
      ))}
      </>
      )}

      {/* ─── Duration Trends Tab ─── */}
      {activeTab === 'trends' && (
      <>
        {trendLoading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <span className="animate-spin mr-2 text-xl">⟳</span>
              <span className="text-muted-foreground">Loading duration trends...</span>
            </CardContent>
          </Card>
        ) : !trendData ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No duration trend data available. Adjust the date range above.
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Percentile Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Percentile:</span>
              <button
                onClick={() => setTrendPercentile('p50')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  trendPercentile === 'p50'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                P50 (Median)
              </button>
              <button
                onClick={() => setTrendPercentile('p90')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  trendPercentile === 'p90'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                P90
              </button>
            </div>

            {/* Anomaly Alerts */}
            {trendData.anomalies?.alerts?.length > 0 && (
              <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-foreground">
                    Anomalies Detected ({trendData.anomalies.alerts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {trendData.anomalies.alerts.slice(0, 5).map((alert, i) => (
                      <div key={i} className="text-xs text-muted-foreground">
                        <span className="font-medium">{alert.metric.replace(/_/g, ' ')}</span>
                        {' '}in {alert.week}: {formatDuration(alert.value)} ({alert.pctChange > 0 ? '+' : ''}{alert.pctChange}% vs baseline {formatDuration(alert.baseline)})
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Duration Trend Line Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Order Step Durations ({trendPercentile.toUpperCase()}) — Weekly</CardTitle>
                <CardDescription>Duration between order lifecycle steps in hours, trended by ISO week</CardDescription>
              </CardHeader>
              <CardContent>
                {trendData.weeks?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={trendData.weeks}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="iso_week" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                      <Tooltip
                        contentStyle={{ fontSize: 12, backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                        formatter={(value) => value != null ? formatDuration(value) : 'N/A'}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {trendData.steps_included?.map((step) => (
                        <Line
                          key={step.key}
                          type="monotone"
                          dataKey={`${trendPercentile}_${step.key}`}
                          name={step.label}
                          stroke={STEP_COLORS[step.key] || '#888'}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No weekly data available</p>
                )}
              </CardContent>
            </Card>

            {/* Per-Clinic Total Order Time */}
            {trendData.clinic_trends?.clinics?.length > 0 && (
              <ClinicTrendCard
                clinicTrends={trendData.clinic_trends}
                stat={clinicTrendStat}
                onStatChange={setClinicTrendStat}
              />
            )}

            {/* Weekly Data Table */}
            {trendData.weeks?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Weekly Detail</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 font-medium">Week</th>
                          {trendData.steps_included?.map(step => (
                            <th key={step.key} className="text-right py-2 px-2 font-medium">{step.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {trendData.weeks.map((w) => {
                          const hasAnomaly = (metric) =>
                            trendData.anomalies?.alerts?.some(a => a.week === w.iso_week && a.metric === metric)
                          return (
                            <tr key={w.iso_week} className="border-b border-border/50 hover:bg-muted/50">
                              <td className="py-1.5 px-2 font-mono">{w.iso_week}</td>
                              {trendData.steps_included?.map(step => {
                                const val = w[`${trendPercentile}_${step.key}`]
                                const count = w[`count_${step.key}`]
                                const anomaly = hasAnomaly(`${trendPercentile}_${step.key}`)
                                return (
                                  <td key={step.key} className={`py-1.5 px-2 text-right ${anomaly ? 'text-amber-600 font-medium' : ''}`}>
                                    {val != null ? formatDuration(val) : '—'}
                                    {count > 0 && <span className="text-muted-foreground ml-1">({count})</span>}
                                    {anomaly && ' ⚠'}
                                  </td>
                                )
                              })}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </>
      )}
    </div>
  )
}

// Per-Clinic Breakdown — P50/P90/completed count for each step, one row per clinic
function ClinicBreakdownCard({ stepData, enableComparison }) {
  const stepKeys = Object.keys(stepData.steps)
  const clinics = stepData.metadata.clinics
  const statFor = (stepKey, clinicId) =>
    stepData.steps[stepKey].byClinic?.find(c => c.clinicId === clinicId)

  return (
    <Card>
      <CardHeader>
        <CardTitle>By Clinic</CardTitle>
        <CardDescription>
          Completed-order P50 / P90 per step for each clinic (count in brackets)
          {enableComparison && '; comparison-period P90 shown underneath'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-2 font-medium">Clinic</th>
                {stepKeys.map(stepKey => (
                  <th key={stepKey} className="text-right py-2 px-2 font-medium whitespace-nowrap">
                    <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: STEP_COLORS[stepKey] }} />
                    {stepData.steps[stepKey].label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clinics.map(clinic => (
                <tr key={clinic.clinicId} className="border-b border-border/50 hover:bg-muted/50">
                  <td className="py-1.5 px-2 font-medium whitespace-nowrap">
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: clinic.color }} />
                    {clinic.displayName}
                  </td>
                  {stepKeys.map(stepKey => {
                    const stat = statFor(stepKey, clinic.clinicId)
                    const completed = stat?.completed
                    return (
                      <td key={stepKey} className="py-1.5 px-2 text-right whitespace-nowrap">
                        {completed?.count > 0 ? (
                          <>
                            {formatDuration(completed.median)} / <span className="font-medium">{formatDuration(completed.p90)}</span>
                            <span className="text-muted-foreground ml-1">({completed.count.toLocaleString()})</span>
                            {enableComparison && stat.comparison?.completed?.count > 0 && (
                              <div className="text-muted-foreground">vs P90 {formatDuration(stat.comparison.completed.p90)}</div>
                            )}
                          </>
                        ) : '—'}
                      </td>
                    )
                  })}
                </tr>
              ))}
              <tr className="font-medium bg-muted/30">
                <td className="py-1.5 px-2">All selected</td>
                {stepKeys.map(stepKey => {
                  const completed = stepData.steps[stepKey].primary?.completed
                  return (
                    <td key={stepKey} className="py-1.5 px-2 text-right whitespace-nowrap">
                      {completed?.count > 0 ? (
                        <>
                          {formatDuration(completed.median)} / {formatDuration(completed.p90)}
                          <span className="text-muted-foreground ml-1">({completed.count.toLocaleString()})</span>
                        </>
                      ) : '—'}
                    </td>
                  )
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// Per-clinic Total Order Time by week, with a switchable statistic
function ClinicTrendCard({ clinicTrends, stat, onStatChange }) {
  const statLabel = CLINIC_TREND_STATS.find(s => s.key === stat)?.label || stat
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle>{clinicTrends.label} by Clinic ({statLabel}) — Weekly</CardTitle>
            <CardDescription>One line per clinic, completed orders bucketed by the ISO week they completed</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {CLINIC_TREND_STATS.map(s => (
              <button
                key={s.key}
                onClick={() => onStatChange(s.key)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  stat === s.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {clinicTrends.weeks.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={clinicTrends.weeks}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="iso_week" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <Tooltip
                contentStyle={{ fontSize: 12, backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                formatter={(value, name, item) => {
                  const clinic = clinicTrends.clinics.find(c => c.displayName === name)
                  const count = clinic && item?.payload?.clinics?.[clinic.clinicId]?.count
                  return [value != null ? `${formatDuration(value)}${count ? ` (${count})` : ''}` : 'N/A', name]
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {clinicTrends.clinics.map(clinic => (
                <Line
                  key={clinic.clinicId}
                  type="monotone"
                  dataKey={(row) => row.clinics?.[clinic.clinicId]?.[stat] ?? null}
                  name={clinic.displayName}
                  stroke={clinic.color || '#888'}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">No weekly data available</p>
        )}
      </CardContent>
    </Card>
  )
}

// Stacked Timing Chart Component - Shows P90, Median, Avg as stacked horizontal bars
function StackedTimingChart({ stepData }) {
  // Prepare data for stacked bar chart
  // Exclude Total_Order_Time and Time_to_Cancel from stacking (they overlap with other steps)
  const stackableSteps = ['Time_at_Doctor', 'Time_at_Pharmacy', 'Time_in_Shipping']
  
  const chartData = [
    { metric: 'Average Time' },
    { metric: 'Median Time' },
    { metric: 'P90 Time' }
  ]
  
  // Calculate totals and populate step values
  let avgTotal = 0, medianTotal = 0, p90Total = 0
  
  stackableSteps.forEach(stepKey => {
    const step = stepData.steps[stepKey]
    if (step?.primary?.completed) {
      const completed = step.primary.completed
      chartData[0][stepKey] = completed.avg || 0
      chartData[1][stepKey] = completed.median || 0
      chartData[2][stepKey] = completed.p90 || 0
      avgTotal += completed.avg || 0
      medianTotal += completed.median || 0
      p90Total += completed.p90 || 0
    }
  })
  
  // Add totals for annotation
  chartData[0].total = avgTotal
  chartData[1].total = medianTotal
  chartData[2].total = p90Total

  // Custom tooltip with proper styling
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[200px]">
        <p className="font-semibold text-foreground mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4 text-sm py-0.5">
            <span className="flex items-center gap-2">
              <span 
                className="w-3 h-3 rounded-sm" 
                style={{ backgroundColor: entry.fill }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
            </span>
            <span className="font-medium text-foreground">{formatDuration(entry.value)}</span>
          </div>
        ))}
        <div className="border-t border-border mt-2 pt-2 flex justify-between items-center">
          <span className="text-muted-foreground text-sm">Total:</span>
          <span className="font-bold text-foreground">
            {formatDuration(payload.reduce((sum, p) => sum + (p.value || 0), 0))}
          </span>
        </div>
      </div>
    )
  }

  const stepLabels = {
    'Time_at_Doctor': 'Time at Doctor',
    'Time_at_Pharmacy': 'Time at Pharmacy',
    'Time_in_Shipping': 'Time in Shipping'
  }

  return (
    <div className="space-y-4">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 120, left: 100, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
            <XAxis 
              type="number" 
              tickFormatter={(v) => `${v.toFixed(0)}h`}
              domain={[0, 'auto']}
            />
            <YAxis 
              type="category" 
              dataKey="metric" 
              width={90} 
              tick={{ fontSize: 12, fill: 'currentColor' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="top"
              formatter={(value) => <span className="text-foreground text-sm">{value}</span>}
            />
            {stackableSteps.map((stepKey) => (
              <Bar 
                key={stepKey}
                dataKey={stepKey} 
                name={stepLabels[stepKey]}
                stackId="stack"
                fill={STEP_COLORS[stepKey]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Summary stats below chart */}
      <div className="grid grid-cols-3 gap-4 text-center pt-2 border-t border-border">
        <div>
          <div className="text-lg font-bold text-foreground">{formatDuration(avgTotal)}</div>
          <div className="text-xs text-muted-foreground">Total Average</div>
        </div>
        <div>
          <div className="text-lg font-bold text-foreground">{formatDuration(medianTotal)}</div>
          <div className="text-xs text-muted-foreground">Total Median</div>
        </div>
        <div className="bg-primary/10 rounded-lg py-2">
          <div className="text-lg font-bold text-primary">{formatDuration(p90Total)}</div>
          <div className="text-xs text-muted-foreground">Total P90</div>
        </div>
      </div>
    </div>
  )
}

// Step Analysis Card Component
function StepAnalysisCard({ stepKey, data, enableComparison, expanded, onToggle, dateRange, compDateRange }) {
  const primary = data.primary
  const comparison = data.comparison
  
  const completed = primary?.completed || {}
  const pending = primary?.pending || {}
  const compCompleted = comparison?.completed || {}
  
  // Prepare histogram data
  const histogramData = []
  const completedDurations = completed.durations || []
  const pendingDurations = pending.durations || []
  
  // Create histogram buckets
  const allDurations = [...completedDurations, ...pendingDurations]
  if (allDurations.length > 0) {
    const maxDuration = Math.max(...allDurations, 24) // At least 24 hours
    const bucketSize = maxDuration / 10
    
    for (let i = 0; i < 10; i++) {
      const bucketStart = i * bucketSize
      const bucketEnd = (i + 1) * bucketSize
      const completedCount = completedDurations.filter(d => d >= bucketStart && d < bucketEnd).length
      const pendingCount = pendingDurations.filter(d => d >= bucketStart && d < bucketEnd).length
      
      histogramData.push({
        range: `${bucketStart.toFixed(0)}-${bucketEnd.toFixed(0)}h`,
        completed: completedCount,
        pending: pendingCount,
      })
    }
  }

  return (
    <Card>
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <span 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: STEP_COLORS[stepKey] }}
              />
              {data.label}
            </CardTitle>
            <CardDescription className="mt-1">
              {data.description} ({data.startStatus} → {data.endStatus})
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            {/* Quick stats */}
            <div className="text-right">
              <div className="text-2xl font-bold text-foreground">{formatDuration(completed.p90)}</div>
              <div className="text-xs text-muted-foreground">P90 Time</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold text-foreground">{(completed.count || 0).toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Completed</div>
            </div>
            {pending.count > 0 && (
              <div className="text-right">
                <div className="text-lg font-semibold text-amber-500">{(pending.count || 0).toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
            )}
            <span className={`transition-transform ${expanded ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent>
          {/* Metrics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
            <MetricCard 
              label="P90 Time" 
              value={formatDuration(completed.p90)} 
              highlight
              comparison={enableComparison ? formatDuration(compCompleted.p90) : null}
            />
            <MetricCard 
              label="Median" 
              value={formatDuration(completed.median)}
              comparison={enableComparison ? formatDuration(compCompleted.median) : null}
            />
            <MetricCard 
              label="Average" 
              value={formatDuration(completed.avg)}
              comparison={enableComparison ? formatDuration(compCompleted.avg) : null}
            />
            <MetricCard 
              label="Completed" 
              value={(completed.count || 0).toLocaleString()}
              comparison={enableComparison ? (compCompleted.count || 0).toLocaleString() : null}
            />
            {stepKey !== 'Time_to_Cancel' && (
              <MetricCard 
                label="Pending" 
                value={(pending.count || 0).toLocaleString()}
                warning={pending.count > 0}
              />
            )}
          </div>
          
          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Histogram */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Duration Distribution</h4>
              <div className="h-64">
                {histogramData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={histogramData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="range" tick={{ fontSize: 10, fill: 'currentColor' }} />
                      <YAxis tick={{ fontSize: 10, fill: 'currentColor' }} />
                      <Tooltip 
                        content={({ active, payload, label }) => {
                          if (!active || !payload) return null
                          return (
                            <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
                              <p className="font-semibold text-foreground mb-2">{label}</p>
                              {payload.map((entry, index) => (
                                <div key={index} className="flex justify-between items-center gap-4 text-sm py-0.5">
                                  <span className="flex items-center gap-2">
                                    <span 
                                      className="w-3 h-3 rounded-sm" 
                                      style={{ backgroundColor: entry.fill }}
                                    />
                                    <span className="text-muted-foreground">{entry.name}:</span>
                                  </span>
                                  <span className="font-medium text-foreground">{entry.value} orders</span>
                                </div>
                              ))}
                            </div>
                          )
                        }}
                      />
                      <Legend 
                        formatter={(value) => <span className="text-foreground text-sm">{value}</span>}
                      />
                      <Bar dataKey="completed" name="Completed" fill={STEP_COLORS[stepKey]} stackId="stack" />
                      <Bar dataKey="pending" name="Pending" fill="#fbbf24" stackId="stack" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    No duration data available
                  </div>
                )}
              </div>
            </div>
            
            {/* Stats Summary */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Statistics Summary</h4>
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <StatRow label="P10" value={formatDuration(completed.p10)} />
                  <StatRow label="P90" value={formatDuration(completed.p90)} />
                  <StatRow label="Median" value={formatDuration(completed.median)} />
                  <StatRow label="Average" value={formatDuration(completed.avg)} />
                </div>
                
                {enableComparison && comparison && (
                  <>
                    <hr className="border-border" />
                    <p className="text-xs text-muted-foreground font-medium">Comparison Period</p>
                    <div className="grid grid-cols-2 gap-4">
                      <StatRow label="P10" value={formatDuration(compCompleted.p10)} />
                      <StatRow label="P90" value={formatDuration(compCompleted.p90)} />
                      <StatRow label="Median" value={formatDuration(compCompleted.median)} />
                      <StatRow label="Average" value={formatDuration(compCompleted.avg)} />
                    </div>
                  </>
                )}
                
                <hr className="border-border" />
                <div className="text-xs text-muted-foreground">
                  <p>Date Range: {dateRange.from?.toLocaleDateString()} - {dateRange.to?.toLocaleDateString()}</p>
                  {enableComparison && compDateRange.from && (
                    <p>Comparison: {compDateRange.from?.toLocaleDateString()} - {compDateRange.to?.toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  )
}

// Metric Card Component
function MetricCard({ label, value, highlight, warning, comparison }) {
  return (
    <div className={`p-4 rounded-lg ${highlight ? 'bg-primary/10 border border-primary/20' : 'bg-muted/30'}`}>
      <div className={`text-xl font-bold ${warning ? 'text-amber-500' : highlight ? 'text-primary' : 'text-foreground'}`}>
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {comparison && (
        <div className="text-xs text-muted-foreground mt-1">
          vs {comparison}
        </div>
      )}
    </div>
  )
}

// Stat Row Component
function StatRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  )
}

